import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Users, 
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  FolderKanban,
  Loader2,
  Plus,
  Search,
  Wifi,
  WifiOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const planConfig = {
  essential: { label: "Essentiel", color: "bg-[#64748b]/20 text-[#94A3B8]" },
  performance: { label: "Performance", color: "bg-[#0099CC]/20 text-[#0099CC]" },
  premium: { label: "Premium", color: "bg-[#F59E0B]/20 text-[#F59E0B]" },
};

const SOCIAL_ICONS = {
  facebook: { label: "FB", color: "#1877F2" },
  instagram: { label: "IG", color: "#E4405F" },
  linkedin: { label: "LI", color: "#0A66C2" },
  tiktok: { label: "TK", color: "#000000" },
  google_business: { label: "GB", color: "#4285F4" },
};

export default function ClientsPage() {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [socialMap, setSocialMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    company_name: "", contact_email: "", contact_phone: "", sector: "", city: "", plan: "essential",
    client_type: "standard",
  });

  useEffect(() => {
    if (isAdmin) {
      fetchClients();
      fetchSocialConnections();
    }
  }, [isAdmin]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSocialConnections = async () => {
    try {
      const response = await axios.get(`${API}/social-connections`);
      const map = {};
      for (const conn of response.data) {
        if (!map[conn.client_id]) map[conn.client_id] = [];
        map[conn.client_id].push(conn);
      }
      setSocialMap(map);
    } catch (error) {
      console.error("Error fetching social connections:", error);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.company_name || !newClient.contact_email) {
      toast.error("Nom et email sont obligatoires");
      return;
    }
    try {
      await axios.post(`${API}/clients`, newClient);
      toast.success("Client cree avec succes");
      setDialogOpen(false);
      setNewClient({ company_name: "", contact_email: "", contact_phone: "", sector: "", city: "", plan: "essential", client_type: "standard" });
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur creation client");
    }
  };

  const filteredClients = clients.filter(client => {
    const q = searchQuery.toLowerCase();
    return client.company_name.toLowerCase().includes(q) ||
      client.sector.toLowerCase().includes(q) ||
      (client.client_number || "").toLowerCase().includes(q) ||
      (client.contact_email || "").toLowerCase().includes(q);
  });

  if (!isAdmin) {
    return (
      <div className="text-center py-20">
        <p className="text-[#94A3B8]">Accès réservé aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Clients</h1>
          <p className="text-[#94A3B8] mt-1">
            {clients.length} entreprise{clients.length > 1 ? 's' : ''} gérée{clients.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="new-client-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouveau client
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher un client..."
          className="pl-10 bg-[#151B2B] border-[#2A3441] text-white"
        />
      </div>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <Users className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun client trouvé</h3>
          <p className="text-[#94A3B8]">
            {searchQuery ? "Modifiez votre recherche" : "Ajoutez votre premier client"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => {
            const plan = planConfig[client.plan] || planConfig.essential;
            
            return (
              <div
                key={client.id}
                className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-6 card-premium"
                data-testid={`client-${client.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#0099CC]/10 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-[#0099CC]" />
                  </div>
                  <Badge className={plan.color}>{plan.label}</Badge>
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-white">{client.company_name}</h3>
                  {client.client_number && (
                    <span className="text-xs font-mono bg-[#0099CC]/10 text-[#0099CC] px-2 py-0.5 rounded">{client.client_number}</span>
                  )}
                </div>
                <p className="text-[#0099CC] text-sm mb-4">{client.sector}
                  <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    client.client_type === "agency" ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-[#64748b]/20 text-[#94A3B8]"
                  }`}>{client.client_type === "agency" ? "AGENCE" : "STANDARD"}</span>
                </p>

                <div className="space-y-2 text-sm">
                  {client.contact_email && (
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <Mail className="h-4 w-4 text-[#64748b]" />
                      <span className="truncate">{client.contact_email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <Phone className="h-4 w-4 text-[#64748b]" />
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client.city && (
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <MapPin className="h-4 w-4 text-[#64748b]" />
                      <span>{client.city}</span>
                    </div>
                  )}
                </div>

                {/* Zone intervention */}
                {client.zone_intervention?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1">
                    {client.zone_intervention.slice(0, 3).map((zone, i) => (
                      <span key={i} className="text-xs bg-[#2A3441] text-[#94A3B8] px-2 py-0.5 rounded">
                        {zone}
                      </span>
                    ))}
                    {client.zone_intervention.length > 3 && (
                      <span className="text-xs text-[#64748b]">+{client.zone_intervention.length - 3}</span>
                    )}
                  </div>
                )}

                {/* Social connections status */}
                {(() => {
                  const conns = socialMap[client.id] || [];
                  const connected = conns.filter(c => c.status === "connected");
                  return (
                    <div className="mt-3 flex items-center gap-1.5" data-testid={`social-status-${client.id}`}>
                      {connected.length > 0 ? (
                        <>
                          <Wifi className="h-3.5 w-3.5 text-green-400" />
                          {connected.map(c => {
                            const info = SOCIAL_ICONS[c.platform] || { label: c.platform, color: "#64748b" };
                            return (
                              <span key={c.platform} className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: info.color + "30", color: info.color }}>
                                {info.label}
                              </span>
                            );
                          })}
                        </>
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] text-[#64748b]">
                          <WifiOff className="h-3 w-3" /> Aucun reseau
                        </span>
                      )}
                    </div>
                  );
                })()}

                <div className="mt-4 pt-4 border-t border-[#2A3441] flex gap-2">
                  <Link to={`/app/clients/${client.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid={`view-client-${client.id}`}>
                      <Building2 className="h-4 w-4 mr-2" />
                      Fiche client
                    </Button>
                  </Link>
                  <Link to={`/app/projets?client_id=${client.id}`}>
                    <Button variant="outline" size="sm" className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid={`view-projects-${client.id}`}>
                      <FolderKanban className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Nouveau client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label className="text-[#94A3B8]">Nom / Entreprise *</Label>
                <Input value={newClient.company_name} onChange={e => setNewClient(p => ({...p, company_name: e.target.value}))}
                  placeholder="Nom de l'entreprise" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="new-client-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Email *</Label>
                <Input type="email" value={newClient.contact_email} onChange={e => setNewClient(p => ({...p, contact_email: e.target.value}))}
                  placeholder="email@exemple.fr" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="new-client-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Telephone</Label>
                <Input value={newClient.contact_phone} onChange={e => setNewClient(p => ({...p, contact_phone: e.target.value}))}
                  placeholder="06 12 34 56 78" className="bg-[#0B0F19] border-[#2A3441] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Type</Label>
                <Select value={newClient.client_type} onValueChange={v => setNewClient(p => ({...p, client_type: v}))}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                    <SelectItem value="standard">Particulier</SelectItem>
                    <SelectItem value="entreprise">Entreprise</SelectItem>
                    <SelectItem value="agence">Agence</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Secteur</Label>
                <Input value={newClient.sector} onChange={e => setNewClient(p => ({...p, sector: e.target.value}))}
                  placeholder="Ex: Restauration" className="bg-[#0B0F19] border-[#2A3441] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Ville</Label>
                <Input value={newClient.city} onChange={e => setNewClient(p => ({...p, city: e.target.value}))}
                  placeholder="Ex: Paris" className="bg-[#0B0F19] border-[#2A3441] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Plan</Label>
                <Select value={newClient.plan} onValueChange={v => setNewClient(p => ({...p, plan: v}))}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="essential">Essential</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button type="submit" className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="save-client-btn">Creer le client</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
